/**
 * <CountdownButton> — un bouton qui se presse seul au bout d'une durée, et qui se remplit pendant
 * ce temps (molecule : `<Button>` + `<TimerBar cover>`).
 *
 * Le motif de l'ancien `TouchableRippleM autoClickDuration` : la suite arrive seule, l'élève peut la
 * prendre avant. Comme lui, c'est le bouton qui dit le temps qui reste : sa face se remplit de la
 * gauche vers la droite, dans le ton foncé de son aplat (son relief), et il se presse quand elle est
 * pleine (Wajih, 21/10 : « La barre, elle peut être sur le bouton »). Le libellé reste blanc sur un
 * fond qui ne fait que foncer : il se lit du début à la fin. La molécule tient le minuteur et le
 * remplissage avec la même durée, pour qu'ils ne se contredisent jamais, et garde la pression unique
 * (le geste et l'échéance ne font partir la suite qu'une fois).
 *
 * Le compte à rebours part quand le bouton est là : `running` (vrai par défaut) et pas `disabled`.
 * Un écran qui montre son bouton plus tard le passe à vrai à ce moment-là.
 *
 * En mouvement réduit, rien ne se remplit : le bouton se presse quand même au bout de la durée, et
 * son libellé dit la suite (l'ancien bouton, lui, se remplissait en mouvement réduit aussi).
 *
 * Pendant le compte, le lecteur d'écran l'apprend (`countdownHint`) : le remplissage ne s'entend pas.
 */
import React from "react"

import { Button, type ButtonProps } from "../../atoms/Buttons/Button"
import { TimerBar } from "../../atoms/TimerBar"
import { reliefOnFill, solidFill } from "../../theme/tokens/colors"
import { countdownHint } from "./countdownHint"
import { useCountdownPress } from "./useCountdownPress"

export type CountdownButtonProps = Omit<ButtonProps, "onPress" | "fullWidth" | "fitContent" | "underlay"> & {
    /** Le geste, ou l'échéance : une fois. */
    onPress: () => void
    /** Le temps avant que le bouton se presse seul, en millisecondes. */
    durationMs: number
    /** Le compte à rebours court — vrai par défaut. Faux : il attend, et repart de zéro ensuite. */
    running?: boolean
}

export function CountdownButton(props: CountdownButtonProps) {
    const { onPress, durationMs, running = true, disabled, accessibilityHint, ...button } = props
    const counting = running && !disabled
    const press = useCountdownPress({ durationMs, running: counting, onPress })
    // Le ton foncé de l'aplat du bouton : celui de son relief.
    const fill = reliefOnFill(solidFill(button.color ?? "role-fill"))

    return (
        <Button
            {...button}
            disabled={disabled}
            onPress={press}
            fullWidth
            accessibilityHint={counting ? countdownHint(durationMs) : accessibilityHint}
            underlay={counting ? <TimerBar durationMs={durationMs} color={fill} cover /> : undefined}
        />
    )
}
